import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { OPTICLOCK_MINUTE_STEP } from '@staff-app/shared-types';
import type { OpticlockEntryInput, OpticlockProject } from '@staff-app/shared-types';
import { DURATION_OPTIONS } from '../lib/duration';
import { ProjectDot } from '../lib/project-colors';
import { todayCentral } from '../lib/month';

/**
 * The stepper's bounds are taken FROM the option list rather than declared separately: stepping
 * past either end would leave the Select with a value it has no option for, which renders as an
 * empty trigger. Tie them together and that cannot happen.
 */
const MIN_MINUTES = DURATION_OPTIONS[0].value;
const MAX_MINUTES = DURATION_OPTIONS[DURATION_OPTIONS.length - 1].value;

interface EntryFormProps {
  projects: OpticlockProject[];
  adding: boolean;
  onAdd: (input: OpticlockEntryInput) => Promise<void>;
}

/**
 * Log one chunk of work: day, category, how long, and an optional note.
 *
 * The minutes field is a Select over DURATION_OPTIONS rather than a number input, so the
 * control CANNOT express a value the 15-minute CHECK constraint would reject — the constraint
 * is the backstop, not the UX.
 *
 * The date defaults to today in CENTRAL (todayCentral), never `new Date()` in the browser's
 * zone: an agent in Vietnam would otherwise default to tomorrow.
 */
export function EntryForm({ projects, adding, onAdd }: EntryFormProps) {
  const [entryDate, setEntryDate] = useState(todayCentral());
  const [projectId, setProjectId] = useState('');
  const [minutes, setMinutes] = useState('60');
  const [note, setNote] = useState('');

  const canSubmit = !!projectId && !!entryDate && !adding;
  const mins = Number(minutes);

  /** Nudge by one 15-minute step, clamped to the option range. */
  function step(delta: number): void {
    const next = Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, mins + delta * OPTICLOCK_MINUTE_STEP));
    setMinutes(String(next));
  }

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    await onAdd({
      projectId,
      entryDate,
      minutes: Number(minutes),
      note: note.trim() || undefined,
    });
    // Keep the day and the category — logging several chunks against the same project on the
    // same day is the common case. Clear only what is specific to the chunk just logged.
    setNote('');
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
      <h2 className="text-sm font-semibold">{'Log time'}</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">{'Day'}</Label>
          <Input
            type="date"
            className="w-[9.5rem]"
            max={todayCentral()}
            value={entryDate}
            aria-label="Day worked"
            data-testid="opticlock-entry-date"
            onChange={(e) => setEntryDate(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">{'Project'}</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-[220px]" data-testid="opticlock-entry-project">
              <SelectValue placeholder="Pick a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p._id} value={p._id}>
                  {/* The dot is the same colour the entry will carry in the bar and the
                      breakdown, so the category is recognisable before it is read. */}
                  <span className="flex items-center gap-2">
                    <ProjectDot color={p.color} />
                    {p.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">{'Time'}</Label>
          {/* Dropdown AND a spinner, deliberately: the list is how you jump to 4:00 without 16
              clicks, the spinner is how you nudge by 15 minutes without hunting in a list. */}
          <div className="flex items-stretch gap-1">
            <Select value={minutes} onValueChange={setMinutes}>
              <SelectTrigger className="w-[110px]" data-testid="opticlock-entry-minutes">
                <SelectValue placeholder="0:15" />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-col justify-center">
              <Button
                type="button"
                variant="outline"
                size="icon-xs"
                className="h-[18px] w-6 rounded-b-none"
                disabled={mins >= MAX_MINUTES}
                aria-label="Add 15 minutes"
                data-testid="opticlock-entry-minutes-up"
                onClick={() => step(1)}
              >
                <ChevronUp />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon-xs"
                className="h-[18px] w-6 rounded-t-none border-t-0"
                disabled={mins <= MIN_MINUTES}
                aria-label="Subtract 15 minutes"
                data-testid="opticlock-entry-minutes-down"
                onClick={() => step(-1)}
              >
                <ChevronDown />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex grow flex-col gap-1">
          <Label className="text-xs text-muted-foreground">{'Note (optional)'}</Label>
          <Input
            value={note}
            placeholder="What did you work on?"
            aria-label="Note"
            data-testid="opticlock-entry-note"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <Button disabled={!canSubmit} data-testid="opticlock-entry-add" onClick={submit}>
          {adding ? 'Adding…' : 'Add'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {'Time is logged in 15-minute steps. One day cannot exceed 24:00 in total.'}
      </p>
    </div>
  );
}
